import Discount from "../../entities/discount";

/**
 * Discount actions
 *
 * Call order:
 *
 * DISCOUNT_SAVE_QUEUE:	adds the item to the save queue
 * --> DISCOUNT_SAVE:	modifies the state
 * --> DISCOUNT_SAVE_SUCCESS: if all went well
 * --> DISCOUNT_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail DISCOUNT_SAVE_QUEUE is during the call to DISCOUNT_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at DISCOUNT_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: DISCOUNT_SAVE_SUCCESS, DISCOUNT_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or DISCOUNT_PENDING_CHECK (it left the queue).
 */

export const DISCOUNT_FETCH_ONE_QUEUE: any = 'DISCOUNT_FETCH_ONE_QUEUE';
export const DISCOUNT_FETCH_SUCCESS: any = 'DISCOUNT_FETCH_SUCCESS';
export const DISCOUNT_FETCH_FAILURE: any = 'DISCOUNT_FETCH_FAILURE';
export const DISCOUNT_FETCH_ONE_SUCCESS: any = 'DISCOUNT_FETCH_ONE_SUCCESS';
export const DISCOUNT_FETCH_ONE_FAILURE: any = 'DISCOUNT_FETCH_ONE_FAILURE';

export const DISCOUNT_SAVE: any = 'DISCOUNT_SAVE';
export const DISCOUNT_SAVE_QUEUE: any = 'DISCOUNT_SAVE_QUEUE';
export const DISCOUNT_SAVE_SUCCESS: any = 'DISCOUNT_SAVE_SUCCESS';
export const DISCOUNT_SAVE_FAILURE: any = 'DISCOUNT_SAVE_FAILURE';

export const DISCOUNT_SAVE_MANY: any = 'DISCOUNT_SAVE_MANY';
export const DISCOUNT_SAVE_MANY_QUEUE: any = 'DISCOUNT_SAVE_MANY_QUEUE';
export const DISCOUNT_SAVE_MANY_SUCCESS: any = 'DISCOUNT_SAVE_MANY_SUCCESS';
export const DISCOUNT_SAVE_MANY_FAILURE: any = 'DISCOUNT_SAVE_MANY_FAILURE';

export const DISCOUNT_REMOVE: any = 'DISCOUNT_REMOVE';
export const DISCOUNT_REMOVE_QUEUE: any = 'DISCOUNT_REMOVE_QUEUE';
export const DISCOUNT_REMOVE_SUCCESS: any = 'DISCOUNT_REMOVE_SUCCESS';
export const DISCOUNT_REMOVE_FAILURE: any = 'DISCOUNT_REMOVE_FAILURE';

export const DISCOUNT_RESTORE: any = 'DISCOUNT_RESTORE';
export const DISCOUNT_RESTORE_QUEUE: any = 'DISCOUNT_RESTORE_QUEUE';
export const DISCOUNT_RESTORE_SUCCESS: any = 'DISCOUNT_RESTORE_SUCCESS';
export const DISCOUNT_RESTORE_FAILURE: any = 'DISCOUNT_RESTORE_FAILURE';

export const DISCOUNT_RESTORE_MANY: any = 'DISCOUNT_RESTORE_MANY';
export const DISCOUNT_REMOVE_MANY: any = 'DISCOUNT_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const DISCOUNT_SAVE_REVERT: any = 'DISCOUNT_SAVE_REVERT';
export const DISCOUNT_PENDING_CHECK: any = 'DISCOUNT_PENDING_CHECK';

/* Fetch actions */
export const fetchDiscountSuccess = (discounts: Discount[], partial: boolean, lastFetch: number) => ({type: DISCOUNT_FETCH_SUCCESS, discounts, partial, lastFetch});
export const fetchDiscountFailure = (err: any) => {
		console.error('DiscountAction: fetchDiscountFailure', err);
		return {type: DISCOUNT_FETCH_FAILURE, err};
};
export const fetchOneDiscountQueue = (discountId: string, sentAddedOn?: number) => ({type: DISCOUNT_FETCH_ONE_QUEUE, discountId, sentAddedOn});
export const fetchOneDiscountSuccess = (discount: Discount | null, sentAddedOn?: number, discountId: string = discount?.id) => ({type: DISCOUNT_FETCH_ONE_SUCCESS, discountId, discount, sentAddedOn});
export const fetchOneDiscountFailure = (id: string, err: any) => {
		console.error('DiscountAction: fetchOneDiscountFailure', err);
		return {type: DISCOUNT_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveDiscount = (discount: Discount) => {
    // console.debug('⮂ DISCOUNT_SAVE action', discount?.id);
    return {type: DISCOUNT_SAVE, discount};
}
export const saveDiscountQueue = (discount: Discount, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ DISCOUNT_SAVE_QUEUE action', discount?.id);
    return {type: DISCOUNT_SAVE_QUEUE, discount, onQueued};
}
export const saveDiscountSuccess = (discount: Discount, sentAddedOn?: number) => {
    // console.debug('⮂ DISCOUNT_SAVE_SUCCESS action', discount?.id);
    return {type: DISCOUNT_SAVE_SUCCESS, discount, sentAddedOn};
}
export const saveDiscountFailure = (discount: Discount, status: number, message: string) => {
    console.warn(`⮂ DISCOUNT_SAVE_FAILURE error saving discount: HTTP ${status}: ${message}`, discount?.id);
    return {type: DISCOUNT_SAVE_FAILURE, discount, status, message};
};

/* SaveMany actions */
export const saveManyDiscounts = (discounts: Discount[]) => {
    // console.debug('⮂ DISCOUNT_SAVE_MANY action', discounts?.map(discount => discount?.id).join(','));
    return {type: DISCOUNT_SAVE_MANY, discounts};
}
export const saveManyDiscountsQueue = (discounts: Discount[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ DISCOUNT_SAVE_MANY_QUEUE action', discounts?.map(discount => discount.id).join(','));
    return {type: DISCOUNT_SAVE_MANY_QUEUE, discounts, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `discounts` (OWA-1018) */
export const saveManyDiscountsSuccess = (discounts: Discount[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ DISCOUNT_SAVE_MANY_SUCCESS action', discounts?.map(discount => discount.id).join(','));
    return {type: DISCOUNT_SAVE_MANY_SUCCESS, discounts, sentAddedOns};
}
export const saveManyDiscountsFailure = (discounts: Discount[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_DISCOUNTS_FAILURE Error saving discounts: HTTP ${status}: ${message}`, discounts?.map(discount => discount.id).join(','));
		return {type: DISCOUNT_SAVE_MANY_FAILURE, discounts, status, message};
};

/* Remove actions */
export const removeDiscount = (discount: Discount) => ({ type: DISCOUNT_REMOVE, discount });
export const removeDiscountQueue = (discount: Discount) => ({ type: DISCOUNT_REMOVE_QUEUE, discount });
export const removeDiscountSuccess = (discount: Discount) => ({ type: DISCOUNT_REMOVE_SUCCESS, discount });
export const removeDiscountFailure = (discount: Discount, status: number, message: string) => {
	console.error(`Redux: removeDiscountFailure Error removing discount: HTTP ${status}: ${message}`);
	return ({ type: DISCOUNT_REMOVE_FAILURE, discount, status, message });
};

/* Restore actions */
export const restoreDiscount = (discount: Discount) => ({ type: DISCOUNT_RESTORE, discount });
export const restoreDiscountQueue = (discount: Discount) => ({ type: DISCOUNT_RESTORE_QUEUE, discount });
export const restoreDiscountSuccess = (discount: Discount, sentAddedOn?: number) => ({ type: DISCOUNT_RESTORE_SUCCESS, discount, sentAddedOn });
export const restoreDiscountFailure = (discount: Discount, status: number, message: string) => {
	console.error(`Redux: restoreDiscountFailure Error restoring discount: HTTP ${status}: ${message}`);
	return ({ type: DISCOUNT_RESTORE_FAILURE, discount, status, message });
};

/* RestoreMany actions */
export const restoreManyDiscounts = (discounts: Discount[]) => ({ type: DISCOUNT_RESTORE_MANY, discounts });

/* RemoveMany actions */
export const removeManyDiscounts = (discounts: Discount[]) => ({ type: DISCOUNT_REMOVE_MANY, discounts });

/* Pending save actions (OWA-1018) */
/**
 * The save `discount` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertDiscountSave = (discount: Discount, options: {sentAddedOn?: number, current?: Discount | null, keep?: boolean} = {}) =>
    ({type: DISCOUNT_SAVE_REVERT, discount, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkDiscountPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: DISCOUNT_PENDING_CHECK, live, refused, readAt});